import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS } from "@/lib/businessInfo";

const NAV_LINKS = [
    { id: "home", key: "nav.home", testId: "nav-home-link" },
    { id: "about", key: "nav.about", testId: "nav-about-link" },
    { id: "services", key: "nav.services", testId: "nav-services-link" },
    { id: "coverage", key: "nav.coverage", testId: "nav-coverage-link" },
    { id: "contact", key: "nav.contact", testId: "nav-contact-link" },
];

const Header = () => {
    const { lang, setLang, t } = useLanguage();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNav = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/90 border-b border-slate-200/70 shadow-[0_4px_30px_rgba(11,48,138,0.06)]"
                    : "bg-white/70 backdrop-blur-md"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 h-[88px] flex items-center justify-between">
                {/* Logo */}
                <button
                    data-testid="brand-logo"
                    onClick={() => handleNav("home")}
                    className="flex items-center group shrink-0"
                    aria-label="Shree Ganesh Financial Service - Home"
                >
                    <img
                        src="/Shree-Ganesh-Logo.png"
                        alt="Shree Ganesh Financial Service"
                        className="h-[60px] sm:h-[68px] w-auto object-contain group-hover:scale-[1.03] transition-transform"
                    />
                </button>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-9">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            data-testid={link.testId}
                            onClick={() => handleNav(link.id)}
                            className={`text-[14px] font-medium text-slate-700 hover:text-[#0B308A] transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-[#0B308A] hover:after:w-full after:transition-all ${
                                lang === "gu" ? "font-gujarati" : ""
                            }`}
                        >
                            {t(link.key)}
                        </button>
                    ))}
                </nav>

                {/* Right side */}
                <div className="flex items-center gap-2 md:gap-3">
                    {/* Language toggle */}
                    <div
                        data-testid="lang-toggle-btn"
                        className="inline-flex items-center bg-slate-100 rounded-full p-1 text-[12px] font-semibold"
                    >
                        <button
                            data-testid="lang-en"
                            onClick={() => setLang("en")}
                            className={`px-3 py-1.5 rounded-full transition-all ${
                                lang === "en"
                                    ? "bg-[#0B308A] text-white shadow-sm"
                                    : "text-slate-600 hover:text-[#0B308A]"
                            }`}
                        >
                            EN
                        </button>
                        <button
                            data-testid="lang-gu"
                            onClick={() => setLang("gu")}
                            className={`px-3 py-1.5 rounded-full transition-all font-gujarati ${
                                lang === "gu"
                                    ? "bg-[#0B308A] text-white shadow-sm"
                                    : "text-slate-600 hover:text-[#0B308A]"
                            }`}
                        >
                            ગુ
                        </button>
                    </div>

                    <a
                        data-testid="header-call-btn"
                        href={`tel:${BUSINESS.phoneTel[0]}`}
                        className={`hidden md:inline-flex items-center gap-2 bg-[#0B308A] text-white hover:bg-[#1E3A8A] transition-colors px-5 py-2.5 rounded-sm font-semibold text-[13px] shadow-[0_6px_18px_rgba(11,48,138,0.25)] ${
                            lang === "gu" ? "font-gujarati" : ""
                        }`}
                    >
                        <Phone className="w-4 h-4" strokeWidth={2.4} />
                        {t("nav.callNow")}
                    </a>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-sm border border-slate-200 text-slate-700 hover:text-[#0B308A] hover:border-[#0B308A]"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl"
                >
                    <div className="px-5 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            <button
                                key={link.id}
                                data-testid={`mobile-${link.testId}`}
                                onClick={() => handleNav(link.id)}
                                className={`text-left px-3 py-3 rounded-sm text-[15px] font-medium text-slate-800 hover:bg-slate-50 hover:text-[#0B308A] ${
                                    lang === "gu" ? "font-gujarati" : ""
                                }`}
                            >
                                {t(link.key)}
                            </button>
                        ))}
                        <a
                            data-testid="mobile-call-btn"
                            href={`tel:${BUSINESS.phoneTel[0]}`}
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0B308A] text-white px-4 py-3 rounded-sm font-semibold text-[14px]"
                        >
                            <Phone className="w-4 h-4" /> {t("nav.callNow")}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
